import apiClient from '@u/apiClient'
import router from '@/router'
import { forgetIntendedPath } from '@/router/utils/loginRedirect'
import Bugsnag from '@bugsnag/js'
import { clearObservedUser } from '@/observability/faro'
import { helper } from '@u/helper.js'
import LoginController from '@/wayfinder/actions/App/Http/Controllers/Authentification/LoginController'
import LogoutController from '@/wayfinder/actions/App/Http/Controllers/Authentification/LogoutController'
import GetAuthUserController from '@/wayfinder/actions/App/Http/Controllers/User/GetAuthUserController'

export default {
  // region login
  login({ commit }, loginData) {
    return new Promise((resolve, reject) => {
      apiClient
        .route(LoginController.post(), loginData.auth_form, {
          headers: {
            accept: 'application/json',
            'content-type': 'application/json'
          }
        })
        .then((response) => {
          // A partner account (api_connection role only) gets a token for the
          // partner endpoints, without the user payload: it cannot use the
          // application, so nothing is stored.
          if (!response.data.auth) {
            reject({ apiAccountOnly: true })
            return
          }

          commit('setAuth', {
            auth: response.data.auth,
            expiration: response.data.expiration,
            token: response.data.token
          })
          localStorage.setItem('token', response.data.token)
          localStorage.setItem('expiration', response.data.expiration)
          Bugsnag.leaveBreadcrumb('connection', response.data.auth.full_name)
          Bugsnag.setUser(
            response.data.auth.id,
            response.data.auth.email,
            response.data.auth.full_name
          )
          resolve(response.data)
        })
        .catch((reason) => reject(reason))
    })
  },
  // endregion

  // region logout
  async logout({ commit, dispatch }, payload) {
    // Revoke the token on the API. An expired token is not sent: the HTTP
    // client would redirect to the login page itself. A failed call must not
    // block the local logout.
    const expiration = localStorage.getItem('expiration')
    if (
      localStorage.getItem('token') &&
      !(expiration && new Date() > new Date(expiration))
    ) {
      await apiClient.route(LogoutController.post()).catch(() => undefined)
    }

    clearObservedUser()
    await commit('setAuth', { auth: {}, expiration: null, errors: [] })
    dispatch('metadata/resetState', payload, { root: true })
    localStorage.removeItem('token')
    localStorage.removeItem('expiration')
    // A token revoked elsewhere gets a 401, whose redirect remembers the
    // current page: a deliberate logout must not reopen it for the next user.
    forgetIntendedPath()
    router.push({ name: 'login' })
  },
  // endregion

  getAuthUser({}) {
    return new Promise((resolve, reject) => {
      apiClient
        .route(GetAuthUserController.get())
        .then((response) => {
          resolve(response.data)
        })
        .catch((reason) => {
          reject(reason)
        })
    })
  },

  updateAuthUser: ({ commit, dispatch }, { data, ...rest }) => {
    const formData = helper.toFormData(rest)
    formData.set('data', JSON.stringify(data))

    return new Promise((resolve, reject) => {
      apiClient
        .post(`/api/auth/user`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        .then((response) => {
          resolve(response.data)
        })
        .catch((reason) => {
          reject(reason)
        })
    })
  },

  // region refreshAuth
  async refreshAuth({ commit }) {
    await apiClient
      .route(GetAuthUserController.get())
      .then((response) => {
        commit('setAuthUser', response.data.user)
        commit('setPermissions')
      })
      .catch((error) => {
        commit('setErrors', error.response)
      })
  },
  // endregion

  // region init pusher
  async initPusher({ commit, state }, payload) {
    apiClient
      .get('/api/getPusherKey')
      .then(async (response) => {
        payload.successCallback(response.data.key)
      })
      .catch((error) => {
        console.error(error)
      })
  },
  // endregion

  setAuthUser: ({ commit }, payload) => {
    commit('setAuthUser', payload.user)
  },
  setAnimation: ({ commit }, payload) => {
    commit('setAnimation', payload)
  },
  updateUserReleaseNoteVersion({ commit }, version) {
    commit('updateReleaseNoteVersion', version)
  },
  updateGuiSettingsTheme({ commit }, theme) {
    commit('setGuiSettingsTheme', theme)
  }
}
